import "../styles/research.css";
import type { LevelCatalog } from "@highmarch/app";
import { researchTreeView } from "@highmarch/app";
import { PlayerProfile, ResearchNodeView, buyResearch, researchLevelOf } from "@highmarch/domain";
import { icon } from "../iconsKit";
import { fmtCompact, fmtCount } from "../kit";
import { lockChipHtml, sheetHeadHtml } from "../components";
import { portraitImg, resourceIconUrl } from "../portraits";
import { toast } from "../toast";
import { economyChanged, economyLink } from "../hub/economyLink";
import { econCue } from "../hub/purchaseFx";
import { ResearchCell, ResearchTab, effectLong, effectShort, initialTab, researchTabs, statIcon, treeLayout, treeName, treeProgressLine } from "./researchModel";
import { notEnoughGold } from "../hub/getGold";

/**
 * Gold Research (M35, docs/RESEARCH.md): the research tree screen. A parchment hub sheet: the header with the gold
 * held, the tower picker strip (the Fortress, then every owned tower, a red count where a node is affordable), a navy
 * plate naming the tree and its progress, then the tree itself — five nodes on a three-column grid joined by lines,
 * each a cream card with what it does and its state: a gold price (open), a lock chip saying what is missing, or
 * RESEARCHED. A tap on a price buys it at once (gold, no builder). Rules: @highmarch/domain meta/research.ts.
 *
 * Opened from a tower's upgrade sheet (its Research row) and from Shop → Gold → Research, through `openResearch`.
 */

/** What the sheet needs: the catalog, the clock, and a way to save and redraw the hub after a purchase. */
export interface ResearchSheetCtx {
  readonly catalog: LevelCatalog;
  readonly now: () => number;
  readonly onChange: () => void;
}

/** Tree geometry (CSS px): one row per tier. */
const ROW_PX = 118;
const STRIP_ART_PX = 40;
const HEAD_ART_PX = 54;
const GOLD_ICON_PX = 20;

const goldImg = (px: number) => `<img class="rgold" decoding="sync" src="${resourceIconUrl("gold")}" alt="gold" style="width:${px}px;height:${px}px">`;

function tabHtml(B: LevelCatalog["balance"], t: ResearchTab, on: boolean): string {
  const art = portraitImg(t.towerId, STRIP_ART_PX, icon("tower", 26, "#fff8e6"));
  const dot = t.affordable > 0 ? `<i class="dot">${t.affordable}</i>` : "";
  const done = t.owned === t.total ? `<i class="rdone">${icon("star", 11)}</i>` : "";
  return `<button type="button" class="rtab${on ? " on" : ""}${B.towers[t.towerIndex].isBase ? " fort" : ""}" data-tower="${t.towerId}" role="tab" aria-selected="${on}" aria-label="${t.name} research, ${t.owned} of ${t.total}">
    <span class="rart">${art}</span><b>${t.name}</b><small>${t.owned}/${t.total}</small>${dot}${done}</button>`;
}

function actionHtml(v: ResearchNodeView, gold: number): string {
  if (v.state === "owned") return `<span class="rowned disp">${icon("star", 14)} RESEARCHED</span>`;
  // On its own tree the tower's name goes without saying: "Level 12", or what is still needed first.
  if (v.state === "locked") return lockChipHtml(v.lockedBy?.startsWith("Needs") ? v.lockedBy : `Level ${v.node.gate}`);
  const short = gold < v.node.gold;
  return `<button type="button" class="btn gold k-price rbuy${short ? " short" : ""}" data-node="${v.node.id}" aria-label="Research ${v.node.name} for ${v.node.gold} gold">${goldImg(GOLD_ICON_PX)}<span class="k-amt">${fmtCount(v.node.gold)}</span></button>`;
}

function cellHtml(B: LevelCatalog["balance"], c: ResearchCell, gold: number): string {
  const v = c.view, n = v.node;
  const cls = ["rnode", `s-${v.state}`, `c${c.col}`, c.capstone ? "cap" : ""].filter(Boolean).join(" ");
  return `<div class="${cls}" style="top:${c.row * ROW_PX}px" data-node="${n.id}" title="${effectLong(B, n)}">
    <div class="rnt"><span class="ric">${icon(statIcon(n), 18)}</span><b class="disp">${n.name}</b></div>
    <span class="reff">${effectShort(n)}</span>
    <div class="ract">${actionHtml(v, gold)}</div></div>`;
}

function treeHtml(B: LevelCatalog["balance"], p: PlayerProfile, towerIndex: number): string {
  const tree = researchTreeView(p, towerIndex);
  const { cells, edges, rows } = treeLayout(tree);
  const X = ["25%", "50%", "75%"];
  // A line leaves the parent card's bottom and enters the child's top.
  const CARD_TOP = 0, CARD_BOTTOM = ROW_PX - 18;
  const lines = edges.map((e) => `<line class="${e.lit ? "lit" : ""}" x1="${X[e.from.col]}" y1="${e.from.row * ROW_PX + CARD_BOTTOM}" x2="${X[e.to.col]}" y2="${e.to.row * ROW_PX + CARD_TOP}"/>`).join("");
  const h = rows * ROW_PX - 16;
  const level = researchLevelOf(p, towerIndex);
  return `<section class="rplate"><div class="rpart">${portraitImg(B.towers[towerIndex].id, HEAD_ART_PX, icon("tower", 30, "#fff8e6"))}</div>
      <div class="rpt"><small class="disp">${treeName(B, towerIndex).toUpperCase()} · LEVEL ${level}</small><b class="disp rpips" aria-label="${tree.owned} of ${tree.nodes.length} researched">${tree.nodes.map((_, i) => `<i class="${i < tree.owned ? "on" : ""}">${icon("star", 16)}</i>`).join("")}<em>${tree.owned}/${tree.nodes.length}</em></b><span>${treeProgressLine(B, tree, level)}</span></div></section>
    <div class="rtree" style="height:${h}px"><svg class="rlines" width="100%" height="${h}" aria-hidden="true">${lines}</svg>${cells.map((c) => cellHtml(B, c, p.gold)).join("")}</div>`;
}

/**
 * Draws the research sheet on `parent` (replacing one already open), on `towerId`'s tree when given (else the tree of
 * the cheapest open node). Returns the sheet.
 */
export function showResearch(parent: HTMLElement, ctx: ResearchSheetCtx, towerId?: string): HTMLElement {
  parent.querySelector(":scope > .sheet.research")?.remove();
  const sheet = document.createElement("div");
  sheet.className = "sheet panel research";
  parent.append(sheet);
  const p = ctx.catalog.profile, B = ctx.catalog.balance;
  let current = initialTab(researchTabs(p, ctx.catalog.highestLevelCleared), p, towerId)?.towerId ?? "";
  const render = (): void => {
    const keep = sheet.querySelector<HTMLElement>(".rbody")?.scrollTop ?? 0, stripKeep = sheet.querySelector<HTMLElement>(".rstrip")?.scrollLeft ?? 0;
    const tabs = researchTabs(p, ctx.catalog.highestLevelCleared);
    const tab = tabs.find((t) => t.towerId === current) ?? tabs[0];
    if (!tab) { sheet.remove(); return; }
    current = tab.towerId;
    const gold = `<div class="pill rgoldpill"><div class="ic img">${goldImg(22)}</div><span>${fmtCompact(p.gold)}</span></div>`;
    sheet.innerHTML = `${sheetHeadHtml({ title: "Research", portrait: icon("star", 36, "#ffd76a"), tint: "navy", extra: gold })}
      <div class="rstrip" role="tablist" aria-label="Towers">${tabs.map((t) => tabHtml(B, t, t.towerId === current)).join("")}</div>
      <div class="rbody">${treeHtml(B, p, tab.towerIndex)}</div>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".rtab")) b.onclick = () => { current = b.dataset.tower ?? current; render(); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".rbuy")) b.onclick = () => buy(b.dataset.node ?? "");
    const body = sheet.querySelector<HTMLElement>(".rbody"), strip = sheet.querySelector<HTMLElement>(".rstrip");
    if (body) body.scrollTop = keep;
    if (strip) { strip.scrollLeft = stripKeep; if (stripKeep === 0) strip.querySelector<HTMLElement>(".rtab.on")?.scrollIntoView?.({ inline: "center", block: "nearest" }); }
  };
  const buy = (id: string): void => {
    const node = B.research.nodes.find((n) => n.id === id);
    if (!node) return;
    const r = buyResearch(p, id);
    if (r === "not enough gold") {
      void notEnoughGold(parent, node.gold - p.gold, sheet);
      return;
    }
    if (typeof r === "string") { render(); return; }
    econCue("buy");
    ctx.onChange();
    toast(parent, `${node.name} researched! ${effectLong(B, node)}`, 2600);
    if (sheet.isConnected) render();
  };
  render();
  return sheet;
}

/**
 * The one way in (the M35 contract, shared with the Shop's Gold page): opens the research tree on `parent`, on
 * `towerId`'s tree when given. Reaches the game through the economy link (ui/hub/economyLink.ts); before boot (or in a
 * test without one) it does nothing.
 */
export function openResearch(parent: HTMLElement, towerId?: string): void {
  const link = economyLink();
  if (!link) return;
  showResearch(parent, { catalog: link.catalog, now: () => link.now(), onChange: () => economyChanged() }, towerId);
}

/**
 * The upgrade sheet's Research row for tower (or Fortress) `towerIndex`: its progress and how many nodes are open, a
 * red count when one is affordable. `data-guide="research"` is Captain Bram's target.
 */
export function researchRowHtml(profile: PlayerProfile, towerIndex: number): string {
  const tree = researchTreeView(profile, towerIndex);
  if (tree.nodes.length === 0) return "";
  const affordable = tree.nodes.filter((v) => v.affordable).length;
  const sub = tree.owned === tree.nodes.length ? "Fully researched" : tree.available > 0 ? `${tree.owned} of ${tree.nodes.length} researched · ${tree.available} ready` : `${tree.owned} of ${tree.nodes.length} researched`;
  return `<button type="button" class="researchbtn" data-guide="research"><span class="ric">${icon("star", 20, "#ffd76a")}</span><span class="rt"><b class="disp">Research</b><small>${sub}</small></span>${affordable > 0 ? `<i class="dot">${affordable}</i>` : ""}${icon("chev", 18)}</button>`;
}
